import { useCallback, useEffect, useState } from "react";
import { captureError } from "@/lib/sentry";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { LANGUAGES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { LightningIcon, ArrowRightIcon, XIcon } from "@phosphor-icons/react";

export default function WhyItMatters() {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [language, setLanguage] = useState(user?.language_preference || "en");
  const [loadingId, setLoadingId] = useState(null);
  const [output, setOutput] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/games");
      setGames(r.data || []);
    } catch (err) {
      captureError("WIM list load failed:", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (gameId) => {
    setLoadingId(gameId);
    setOutput(null);
    try {
      const res = await api.post(`/games/${gameId}/why-it-matters`, { language });
      setOutput({ gameId, text: res.data.text, game: res.data.game });
    } catch (err) {
      captureError("WIM failed:", err);
      setOutput({ gameId, text: "Generation failed. Try again." });
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <AppLayout>
      <div className="mb-6 fade-up flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-2">Why It Matters</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">One-tap explainer</h1>
          <p className="text-white/60 text-sm mt-2">Pick a game · get the broadcaster's take on why this moment matters.</p>
        </div>
        <select
          data-testid="wim-lang"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="bg-black border border-white/15 text-white/80 text-xs uppercase font-bold tracking-wider rounded-md px-3 py-2"
        >
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
        {games.map((g) => (
          <button
            key={g.id}
            data-testid={`wim-pick-${g.id}`}
            onClick={() => run(g.id)}
            disabled={!!loadingId}
            className="text-left p-4 rounded-md bg-[#121212] border border-white/10 hover:border-[#0A84FF]/50 transition-colors disabled:opacity-50"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">{g.league}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">{g.period_label}</span>
            </div>
            <p className="font-display font-bold text-base uppercase tracking-tight">{g.away_team_abbr} @ {g.home_team_abbr}</p>
            <p className="text-[11px] text-white/55 mt-1 line-clamp-2">{g.key_matchup}</p>
            <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#0A84FF]">
              <LightningIcon weight="fill" /> {loadingId === g.id ? "Thinking…" : "Why it matters"} <ArrowRightIcon size={10} />
            </div>
          </button>
        ))}
        {games.length === 0 && <p className="text-white/50 text-sm">Loading today's slate…</p>}
      </div>

      {output && (
        <div className="p-5 rounded-md bg-[#121212] border border-[#32D74B]/30" data-testid="wim-output">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#32D74B]">Gamly Says · {output.game?.away_team_abbr || ""} @ {output.game?.home_team_abbr || ""}</p>
            <button onClick={() => setOutput(null)} className="text-white/40 hover:text-white"><XIcon size={14} /></button>
          </div>
          <pre className="whitespace-pre-wrap font-medium text-sm text-white/90 leading-relaxed font-sans">{output.text}</pre>
        </div>
      )}
    </AppLayout>
  );
}
